import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
    Alert,
    AlertTitle,
    Tooltip as MuiTooltip,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    ArrowDropUp as ArrowDropUpIcon,
    ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';

export default function AnalyticsDaily() {
    const { error: showError } = useNotify();

    const formatYmd = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const today = new Date();
    const minus7 = new Date(today);
    minus7.setDate(today.getDate() - 7);

    const [filters, setFilters] = useState({});
    const [dateFrom, setDateFrom] = useState(formatYmd(minus7));
    const [dateTo, setDateTo] = useState(formatYmd(today));
    const [rows, setRows] = useState([]);
    const [missingOutgoingNds, setMissingOutgoingNds] = useState({ count: 0, items: [] });
    const [expanded, setExpanded] = useState({});
    const [loading, setLoading] = useState(false);
    const [showDelta, setShowDelta] = useState(true);
    const [chartMetrics, setChartMetrics] = useState(['ordersCount']);

    // Styles
    const cellStyle = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

    // Number formatting
    const fmtNum = (v, decimals = 0) => {
        if (v == null || v === '') return '-';
        const n = Number(v);
        if (isNaN(n)) return '-';
        return n.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };
    const fmtMoney = (v) => fmtNum(v, 2);
    const fmtPct = (v) => {
        if (v == null || v === '') return '-';
        return `${fmtNum(v, 2)}%`;
    };
    const articleWord = (n) => {
        const n10 = n % 10;
        const n100 = n % 100;
        if (n10 === 1 && n100 !== 11) return 'артикула';
        if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return 'артикула';
        return 'артикулов';
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = { ...buildFilterParams(filters) };
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;

            const resp = await axios.get('/analytics/wb/statistic/daily', { params });
            setRows(resp.data.rows || []);
            setMissingOutgoingNds(resp.data.missingOutgoingNds || { count: 0, items: [] });
            setExpanded({});
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    // Toggle row expand to show products
    const toggleDate = async (dateKey) => {
        if (expanded[dateKey]) {
            setExpanded(prev => { const c = { ...prev }; delete c[dateKey]; return c; });
            return;
        }
        try {
            const params = { ...buildFilterParams(filters), date: dateKey };
            const resp = await axios.get('/analytics/wb/statistic/by-product', { params });
            setExpanded(prev => ({ ...prev, [dateKey]: resp.data.rows || [] }));
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки товаров');
        }
    };

    // Metric groups - marketplace specific
    // Подсказки к строкам таблицы. Заказы на всех МП считаются по единому правилу:
    // минус отмены до отправки (МП отменил, мы не отгрузили); % выкупа — единый
    // модуль packer_analytics (unifiedBuyout) по заказам 14–44 дней назад.
    const METRIC_HINTS = {
        ordersCount: 'Заказы за вычетом отменённых до отправки (МП отменил заказ, мы его не отгрузили — за такие ничего не платим). Всё, что отгружено, считается заказом.',
        ordersSumRub: 'Сумма заказов без отменённых до отправки.',
        buyoutsCount: 'Прогноз: заказы × % выкупа карточки. Дробное число — не округляем.',
        buyoutsSumRub: 'Прогноз: сумма заказов × % выкупа карточки.',
        buyoutPercent: 'Единое правило для WB/Ozon/Яндекс: доставлено ÷ (заказы − отмены до отправки − ещё в пути) по заказам 14–44 дней назад. Карточки с < 5 заказов получают средний % по МП.',
        cancellationsCount: 'Отмены до отправки — уже исключены из заказов, показаны справочно.',
        ndsOutSum: 'НДС с продаж: оплата покупателя (с учётом СПП / скидки Маркета) × 22/122, как в финотчёте. С 01.09.2026.',
        ndsInMpSum: 'НДС к вычету по услугам МП: комиссия после выкупа, логистика, реклама × 22/122. Эквайринг без НДС и в базу не входит. У WB минус скидка СПП — может быть отрицательным, как в финотчёте.',
        ndsInProdSum: 'НДС к вычету по производству: себестоимость после выкупа × исходящий НДС карточки (нет — 13%).',
        ndsSum: 'НДС к уплате = с продаж − к вычету МП − к вычету производство. До 01.09.2026 — прежняя формула по ставке кабинета.',
        acquiringSum: 'Эквайринг после выкупа: фактическая ставка из финотчёта за заказы 14–44 дня назад, % от выручки (Ozon/Яндекс — по SKU, WB — по организации). Вычитается из профита, НДС не содержит. WB до 01.09.2026 — внутри комиссии.',
        logistic: 'Прямая + обратная логистика. Ozon: факт по SKU из финотчёта (с последней милей и обработкой), тариф только для SKU без истории. Яндекс: из отчёта.',
        cost: 'От прогноза выкупов: себестоимость заказов (без отмен до отправки) × % выкупа карточки — та сумма, что вычитается из профита.',
        comission: 'От прогноза выкупов: комиссия × % выкупа карточки. WB/Ozon: комиссия с цены (WB — из листа «КомиссияВБ», эквайринг отдельной строкой с 01.09.2026). Яндекс: только комиссия деньгами (~3%) по SKU из финотчёта; скидка Маркета показана отдельно в «Заказах».',
        marketDiscountSum: 'Скидка Маркета (бонусы/баллы, которые Яндекс списывает с нас) = сумма выкупов − оплата покупателя. Берётся из цены с СПП по дате заказа.',
        buyerPaidSum: 'Оплата покупателя после выкупа (цена с СПП × % выкупа). Профит Яндекса считается от неё.',
        profit: 'Сумма выкупов − НДС к уплате − логистика − комиссия − себестоимость − эквайринг − реклама. Яндекс: от оплаты покупателя.',
    };
    const OZON_CANCELLATIONS_HINT = 'Отмены по данным аналитики Ozon (по дате события), справочно.';

    const metricGroupsOzon = {
        views: {
            label: 'Просмотры',
            metrics: [
                { key: 'openCardCount', label: 'Просмотры всего' },
                { key: 'hitsViewSearch', label: 'Просмотры поиск/кат.' },
                { key: 'hitsViewPdp', label: 'Просмотры карточки' },
            ]
        },
        unique: {
            label: 'Уникальные',
            metrics: [
                { key: 'sessionView', label: 'Сессии всего' },
                { key: 'sessionViewSearch', label: 'Сессии поиск/кат.' },
                { key: 'sessionViewPdp', label: 'Сессии карточки' },
            ]
        },
        cart: {
            label: 'Корзина',
            metrics: [
                { key: 'addToCartCount', label: 'В корзину всего' },
                { key: 'hitsTocartSearch', label: 'В корзину поиск/кат.' },
                { key: 'hitsTocartPdp', label: 'В корзину карточка' },
                { key: 'crCart', label: 'CR корзины всего %' },
                { key: 'crCartSearch', label: 'CR корзины поиск/кат. %' },
                { key: 'crCartPdp', label: 'CR корзины карточка %' },
            ]
        },
        orders: {
            label: 'Заказы',
            metrics: [
                { key: 'ordersCount', label: 'Заказов' },
                { key: 'ordersSumRub', label: 'Сумма заказов' },
                { key: 'avgPrice', label: 'Средняя цена' },
                { key: 'avgPriceSpp', label: 'Средняя цена с СПП' },
                { key: 'sppPercent', label: '% СПП' },
                { key: 'crOrder', label: 'CR заказа всего %' },
            ]
        },
        buyouts: {
            label: 'Выкупы',
            metrics: [
                { key: 'buyoutsCount', label: 'Выкупов' },
                { key: 'buyoutsSumRub', label: 'Сумма выкупов' },
                { key: 'buyoutPercent', label: '% выкупа' },
                { key: 'returnsCount', label: 'Возвраты' },
                { key: 'cancellationsCount', label: 'Отмены', hint: OZON_CANCELLATIONS_HINT },
            ]
        },
        advert: {
            label: 'Реклама',
            metrics: [
                { key: 'advViews', label: 'Показы' },
                { key: 'advClicks', label: 'Клики' },
                { key: 'advCtr', label: 'CTR %' },
                { key: 'advCpc', label: 'CPC' },
                { key: 'advSum', label: 'Расход' },
                { key: 'advCpcSum', label: '↳ За клик' },
                { key: 'advCpoSum', label: '↳ За заказ' },
                { key: 'drr', label: 'ДРР %' },
            ]
        },
        finance: {
            label: 'Финансы',
            metrics: [
                { key: 'cost', label: 'Себестоимость' },
                { key: 'comission', label: 'Комиссия' },
                { key: 'logistic', label: 'Логистика' },
                { key: 'acquiringSum', label: 'Эквайринг' },
                { key: 'ndsOutSum', label: 'НДС с продаж' },
                { key: 'ndsInMpSum', label: 'НДС к вычету МП' },
                { key: 'ndsInProdSum', label: 'НДС к вычету производство' },
                { key: 'ndsSum', label: 'НДС к уплате' },
                { key: 'profit', label: 'Профит' },
                { key: 'roi', label: 'ROI %' },
            ]
        },
        position: {
            label: 'Позиция',
            metrics: [
                { key: 'positionCategory', label: 'Позиция в категории' },
            ]
        },
    };

    const metricGroupsAll = {
        cart: {
            label: 'Корзина',
            metrics: [
                { key: 'addToCartCount', label: 'В корзину' },
            ]
        },
        orders: {
            label: 'Заказы',
            metrics: [
                { key: 'ordersCount', label: 'Заказов' },
                { key: 'ordersSumRub', label: 'Сумма заказов' },
                { key: 'avgPrice', label: 'Средняя цена' },
            ]
        },
        buyouts: {
            label: 'Выкупы',
            metrics: [
                { key: 'buyoutsCount', label: 'Выкупов' },
                { key: 'buyoutsSumRub', label: 'Сумма выкупов' },
                { key: 'buyoutPercent', label: '% выкупа' },
            ]
        },
        advert: {
            label: 'Реклама',
            metrics: [
                { key: 'advSum', label: 'Расход' },
                { key: 'advCpcSum', label: '↳ Ozon за клик' },
                { key: 'advCpoSum', label: '↳ Ozon за заказ' },
                { key: 'drr', label: 'ДРР %' },
            ]
        },
        finance: {
            label: 'Финансы',
            metrics: [
                { key: 'cost', label: 'Себестоимость' },
                { key: 'comission', label: 'Комиссия' },
                { key: 'logistic', label: 'Логистика' },
                { key: 'acquiringSum', label: 'Эквайринг' },
                { key: 'ndsOutSum', label: 'НДС с продаж' },
                { key: 'ndsInMpSum', label: 'НДС к вычету МП' },
                { key: 'ndsInProdSum', label: 'НДС к вычету производство' },
                { key: 'ndsSum', label: 'НДС к уплате' },
                { key: 'profit', label: 'Профит' },
                { key: 'roi', label: 'ROI %' },
            ]
        },
    };

    const metricGroupsWB = {
        views: {
            label: 'Клики',
            metrics: [
                { key: 'openCardCount', label: 'Клики' },
            ]
        },
        cart: {
            label: 'Корзина',
            metrics: [
                { key: 'addToCartCount', label: 'В корзину' },
                { key: 'addToCartConversion', label: 'CR корзины %' },
            ]
        },
        orders: {
            label: 'Заказы',
            metrics: [
                { key: 'ordersCount', label: 'Заказов' },
                { key: 'ordersSumRub', label: 'Сумма заказов' },
                { key: 'avgPrice', label: 'Средняя цена' },
                { key: 'avgPriceSpp', label: 'Средняя цена с СПП' },
                { key: 'sppPercent', label: '% СПП' },
                { key: 'cartToOrderConversion', label: 'CR заказа %' },
            ]
        },
        traffic: {
            label: 'Источники трафика',
            metrics: [
                { key: 'searchOpenCard', label: 'Поиск: клики' },
                { key: 'searchAddToCart', label: 'Поиск: в корзину' },
                { key: 'searchCartCr', label: 'Поиск: CR корзины %' },
                { key: 'searchOrders', label: 'Поиск: заказов' },
                { key: 'searchOrderCr', label: 'Поиск: CR заказа %' },
                { key: 'searchClickShare', label: 'Поиск: доля кликов %' },
                { key: 'recoOpenCard', label: 'Рекомендации: клики' },
                { key: 'recoAddToCart', label: 'Рекомендации: в корзину' },
                { key: 'recoCartCr', label: 'Рекомендации: CR корзины %' },
                { key: 'recoOrders', label: 'Рекомендации: заказов' },
                { key: 'recoOrderCr', label: 'Рекомендации: CR заказа %' },
                { key: 'recoClickShare', label: 'Рекомендации: доля кликов %' },
            ]
        },
        buyouts: {
            label: 'Выкупы',
            metrics: [
                { key: 'buyoutsCount', label: 'Выкупов' },
                { key: 'buyoutsSumRub', label: 'Сумма выкупов' },
                { key: 'buyoutPercent', label: '% выкупа' },
                { key: 'cancellationsCount', label: 'Отмены до отправки' },
            ]
        },
        advert: {
            label: 'Реклама',
            metrics: [
                { key: 'advViews', label: 'Показы' },
                { key: 'advClicks', label: 'Клики' },
                { key: 'advCtr', label: 'CTR %' },
                { key: 'advCpc', label: 'CPC' },
                { key: 'advSum', label: 'Расход' },
                { key: 'drr', label: 'ДРР %' },
            ]
        },
        finance: {
            label: 'Финансы',
            metrics: [
                { key: 'cost', label: 'Себестоимость' },
                { key: 'comission', label: 'Комиссия' },
                { key: 'logistic', label: 'Логистика' },
                { key: 'acquiringSum', label: 'Эквайринг' },
                { key: 'ndsOutSum', label: 'НДС с продаж' },
                { key: 'ndsInMpSum', label: 'НДС к вычету МП' },
                { key: 'ndsInProdSum', label: 'НДС к вычету производство' },
                { key: 'ndsSum', label: 'НДС к уплате' },
                { key: 'profit', label: 'Профит' },
                { key: 'roi', label: 'ROI %' },
            ]
        },
    };

    const metricGroupsYandex = {
        views: {
            label: 'Просмотры',
            metrics: [
                { key: 'openCardCount', label: 'Показы' },
                { key: 'hitsViewPdp', label: 'Клики' },
                { key: 'ctr', label: 'CTR %' },
            ]
        },
        cart: {
            label: 'Корзина',
            metrics: [
                { key: 'addToCartCount', label: 'В корзину' },
                { key: 'crCart', label: 'CR корзины %' },
            ]
        },
        orders: {
            label: 'Заказы',
            metrics: [
                { key: 'ordersCount', label: 'Заказов' },
                { key: 'ordersSumRub', label: 'Сумма заказов' },
                { key: 'avgPrice', label: 'Средняя цена' },
                { key: 'avgPriceSpp', label: 'Средняя цена с СПП' },
                { key: 'sppPercent', label: '% СПП' },
                { key: 'buyerPaidSum', label: 'Оплата покупателя' },
                { key: 'marketDiscountSum', label: 'Скидка Маркета' },
                { key: 'crOrder', label: 'CR заказа %' },
            ]
        },
        buyouts: {
            label: 'Выкупы',
            metrics: [
                { key: 'buyoutsCount', label: 'Выкупов' },
                { key: 'buyoutsSumRub', label: 'Сумма выкупов' },
                { key: 'buyoutPercent', label: '% выкупа' },
                { key: 'returnsCount', label: 'Возвраты' },
                { key: 'cancellationsCount', label: 'Отмены до отправки' },
            ]
        },
        advert: {
            label: 'Реклама',
            metrics: [
                { key: 'advViews', label: 'Показы' },
                { key: 'advClicks', label: 'Клики' },
                { key: 'advCtr', label: 'CTR %' },
                { key: 'advCpc', label: 'CPC' },
                { key: 'advSum', label: 'Расход итого' },
                { key: 'advShowsBoostSum', label: '↳ Буст показов' },
                { key: 'advBoostSalesSum', label: '↳ Буст продаж' },
                { key: 'advShelfSum', label: '↳ Полки' },
                { key: 'drr', label: 'ДРР %' },
            ]
        },
        finance: {
            label: 'Финансы',
            metrics: [
                { key: 'cost', label: 'Себестоимость' },
                { key: 'comission', label: 'Комиссия' },
                { key: 'logistic', label: 'Логистика' },
                { key: 'acquiringSum', label: 'Эквайринг' },
                { key: 'ndsOutSum', label: 'НДС с продаж' },
                { key: 'ndsInMpSum', label: 'НДС к вычету МП' },
                { key: 'ndsInProdSum', label: 'НДС к вычету производство' },
                { key: 'ndsSum', label: 'НДС к уплате' },
                { key: 'profit', label: 'Профит' },
                { key: 'roi', label: 'ROI %' },
            ]
        },
    };

    const isAllMp = !filters.mp || filters.mp === '0';
    const metricGroups = isAllMp ? metricGroupsAll : (filters.mp === '2' ? metricGroupsOzon : (filters.mp === '3' ? metricGroupsYandex : metricGroupsWB));

    // Toggle state for groups
    const [enabledGroups, setEnabledGroups] = useState({
        views: true,
        unique: false,
        cart: true,
        orders: true,
        traffic: true,
        buyouts: true,
        advert: true,
        finance: true,
        position: false,
    });

    const toggleGroup = (groupKey) => {
        setEnabledGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    // Headers - dynamic based on marketplace and enabled groups
    const headers = useMemo(() => {
        const result = [];
        Object.entries(metricGroups).forEach(([groupKey, group]) => {
            if (enabledGroups[groupKey]) {
                group.metrics.forEach(m => result.push({ ...m, hint: m.hint ?? METRIC_HINTS[m.key], align: 'right', group: groupKey }));
            }
        });
        return result;
    }, [filters.mp, enabledGroups, metricGroups]);

    const productHeaders = useMemo(() => {
        const base = [
            { key: 'article_number', label: 'Артикул' },
            { key: 'nmID', label: 'nmID' },
            { key: 'brand', label: 'Бренд' },
            { key: 'gruppa', label: 'Группа' },
            { key: 'podgruppa', label: 'Подгруппа' },
            { key: 'openCardCount', label: 'Просмотры', align: 'right' },
        ];

        if (filters.mp === '2') {
            base.push(
                { key: 'hitsViewSearch', label: 'Просм. поиск/кат.', align: 'right' },
                { key: 'hitsViewPdp', label: 'Просм. карточки', align: 'right' },
                { key: 'sessionView', label: 'Сессии всего', align: 'right' },
                { key: 'sessionViewSearch', label: 'Сессии поиск/кат.', align: 'right' },
                { key: 'sessionViewPdp', label: 'Сессии карточки', align: 'right' },
            );
        }

        base.push(
            { key: 'addToCartCount', label: 'В корзину', align: 'right' },
            { key: 'ordersCount', label: 'Заказов', align: 'right' },
            { key: 'ordersSumRub', label: 'Сумма заказов', align: 'right' },
            { key: 'avgPrice', label: 'Средняя цена', align: 'right' },
            { key: 'buyoutsCount', label: 'Выкупов', align: 'right' },
            { key: 'buyoutsSumRub', label: 'Сумма выкупов', align: 'right' },
        );

        if (filters.mp === '2') {
            base.push(
                { key: 'returnsCount', label: 'Возвраты', align: 'right' },
                { key: 'cancellationsCount', label: 'Отмены', align: 'right' },
                { key: 'positionCategory', label: 'Позиция', align: 'right' },
                { key: 'advSum', label: 'Реклама', align: 'right' },
                { key: 'advCpcSum', label: 'За клик', align: 'right' },
                { key: 'advCpoSum', label: 'За заказ', align: 'right' },
            );
        }

        base.push(
            { key: 'cost', label: 'Себестоим.', align: 'right' },
            { key: 'comission', label: 'Комиссия', align: 'right' },
            { key: 'logistic', label: 'Логистика', align: 'right' },
            { key: 'profit', label: 'Профит', align: 'right' },
        );

        return base;
    }, [filters.mp]);

    const getClicksCount = (row) => {
        // In "all" mode backend normalizes openCardCount to clicks across marketplaces.
        if (filters.mp === '2' || filters.mp === '3') return Number(row.hitsViewPdp || 0);
        return Number(row.openCardCount || 0);
    };

    // Totals
    const totals = useMemo(() => {
        const s = rows.reduce((acc, r) => {
            // Views
            acc.openCardCount += Number(r.openCardCount || 0);
            acc.hitsViewSearch += Number(r.hitsViewSearch || 0);
            acc.hitsViewPdp += Number(r.hitsViewPdp || 0);
            // Unique
            acc.sessionView += Number(r.sessionView || 0);
            acc.sessionViewSearch += Number(r.sessionViewSearch || 0);
            acc.sessionViewPdp += Number(r.sessionViewPdp || 0);
            // Cart
            acc.addToCartCount += Number(r.addToCartCount || 0);
            acc.hitsTocartSearch += Number(r.hitsTocartSearch || 0);
            acc.hitsTocartPdp += Number(r.hitsTocartPdp || 0);
            // Traffic breakdown (WB): raw search funnel sums
            acc.searchOpenCard += Number(r.searchOpenCard || 0);
            acc.searchAddToCart += Number(r.searchAddToCart || 0);
            acc.searchOrders += Number(r.searchOrders || 0);
            // Orders
            acc.ordersCount += Number(r.ordersCount || 0);
            acc.ordersSumRub += Number(r.ordersSumRub || 0);
            acc.sppSum += Number(r.sppSum || 0);
            acc.sppQty += Number(r.sppQty || 0);
            // Buyouts
            acc.buyoutsCount += Number(r.buyoutsCount || 0);
            acc.buyoutsSumRub += Number(r.buyoutsSumRub || 0);
            acc.returnsCount += Number(r.returnsCount || 0);
            acc.cancellationsCount += Number(r.cancellationsCount || 0);
            acc._buyoutWeighted += Number(r.buyoutPercent || 0) * Number(r.ordersCount || 0);
            // Finance
            acc.cost += Number(r.cost || 0);
            acc.comission += Number(r.comission || 0);
            acc.logistic += Number(r.logistic || 0);
            acc.ndsSum += Number(r.ndsSum || 0);
            acc.ndsOutSum += Number(r.ndsOutSum || 0);
            acc.ndsInMpSum += Number(r.ndsInMpSum || 0);
            acc.ndsInProdSum += Number(r.ndsInProdSum || 0);
            acc.acquiringSum += Number(r.acquiringSum || 0);
            acc.marketDiscountSum += Number(r.marketDiscountSum || 0);
            acc.buyerPaidSum += Number(r.buyerPaidSum || 0);
            acc.advSum += Number(r.advSum || 0);
            acc.advCpcSum += Number(r.advCpcSum || 0);
            acc.advCpoSum += Number(r.advCpoSum || 0);
            acc.advViews += Number(r.advViews || 0);
            acc.advClicks += Number(r.advClicks || 0);
            acc.advShowsBoostSum += Number(r.advShowsBoostSum || 0);
            acc.advBoostSalesSum += Number(r.advBoostSalesSum || 0);
            acc.advShelfSum += Number(r.advShelfSum || 0);
            acc.profit += Number(r.profit || 0);
            acc._positionSum += Number(r.positionCategory || 0);
            acc._positionCount += (r.positionCategory ? 1 : 0);
            acc._clicksForOrderCr += getClicksCount(r);
            return acc;
        }, {
            openCardCount: 0, hitsViewSearch: 0, hitsViewPdp: 0,
            sessionView: 0, sessionViewSearch: 0, sessionViewPdp: 0,
            addToCartCount: 0, hitsTocartSearch: 0, hitsTocartPdp: 0,
            searchOpenCard: 0, searchAddToCart: 0, searchOrders: 0,
            ordersCount: 0, ordersSumRub: 0, sppSum: 0, sppQty: 0,
            buyoutsCount: 0, buyoutsSumRub: 0, returnsCount: 0, cancellationsCount: 0,
            cost: 0, comission: 0, logistic: 0, ndsSum: 0, ndsOutSum: 0, ndsInMpSum: 0, ndsInProdSum: 0, acquiringSum: 0,
            marketDiscountSum: 0, buyerPaidSum: 0, advSum: 0, advCpcSum: 0, advCpoSum: 0, advViews: 0, advClicks: 0,
            advShowsBoostSum: 0, advBoostSalesSum: 0, advShelfSum: 0,
            profit: 0, _buyoutWeighted: 0, _positionSum: 0, _positionCount: 0, _clicksForOrderCr: 0
        });

        if (filters.mp === '2') {
            s.crCart = s.openCardCount ? +(100 * s.addToCartCount / s.openCardCount).toFixed(2) : 0;
            s.crCartSearch = s.hitsViewSearch ? +(100 * s.hitsTocartSearch / s.hitsViewSearch).toFixed(2) : 0;
            s.crCartPdp = s.hitsViewPdp ? +(100 * s.hitsTocartPdp / s.hitsViewPdp).toFixed(2) : 0;
            s.crOrder = s.openCardCount ? +(100 * s.ordersCount / s.openCardCount).toFixed(2) : 0;
        } else {
            // WB/Yandex keep their marketplace-specific meaning for views/clicks.
            s.ctr = s.openCardCount ? +(100 * s.hitsViewPdp / s.openCardCount).toFixed(2) : 0;
            s.crCart = s.hitsViewPdp ? +(100 * s.addToCartCount / s.hitsViewPdp).toFixed(2) : 0;
            s.crOrder = s._clicksForOrderCr ? +(100 * s.ordersCount / s._clicksForOrderCr).toFixed(2) : 0;
        }

        s.avgPrice = s.ordersCount ? +(s.ordersSumRub / s.ordersCount).toFixed(2) : 0;
        s.avgPriceSpp = s.sppQty ? +(s.sppSum / s.sppQty).toFixed(2) : 0;
        // СПП не бывает отрицательным: если цена с СПП выше нашей — данные битые/расходятся, показываем 0.
        s.sppPercent = (s.avgPrice && s.sppQty)
            ? +Math.max(0, ((s.avgPrice - s.avgPriceSpp) / s.avgPrice) * 100).toFixed(2)
            : 0;
        s.buyoutPercent = s.ordersCount ? +(s._buyoutWeighted / s.ordersCount).toFixed(2) : 0;
        s.positionCategory = s._positionCount ? +(s._positionSum / s._positionCount).toFixed(1) : 0;

        // Calculate CR for WB/all: orders divided by clicks.
        s.addToCartConversion = s.openCardCount ? +(100 * s.addToCartCount / s.openCardCount).toFixed(2) : 0;
        s.cartToOrderConversion = s._clicksForOrderCr ? +(100 * s.ordersCount / s._clicksForOrderCr).toFixed(2) : 0;

        // Calculate ROI = profit / cost * 100
        s.roi = s.cost ? +(100 * s.profit / s.cost).toFixed(2) : 0;

        // Calculate Adv metrics
        s.advCtr = s.advViews ? +(100 * s.advClicks / s.advViews).toFixed(2) : 0;
        s.advCpc = s.advClicks
            ? +((filters.mp === '2' ? (s.advCpcSum || 0) : s.advSum) / s.advClicks).toFixed(2)
            : 0;
        s.drr = s.ordersSumRub ? +(100 * s.advSum / s.ordersSumRub).toFixed(2) : 0;

        // Traffic breakdown (WB): Рекомендации = воронка - поиск; CR и доли считаем на агрегатах
        s.recoOpenCard = Math.max(0, s.openCardCount - s.searchOpenCard);
        s.recoAddToCart = Math.max(0, s.addToCartCount - s.searchAddToCart);
        s.recoOrders = Math.max(0, s.ordersCount - s.searchOrders);
        s.searchCartCr = s.searchOpenCard ? +(100 * s.searchAddToCart / s.searchOpenCard).toFixed(2) : 0;
        s.searchOrderCr = s.searchOpenCard ? +(100 * s.searchOrders / s.searchOpenCard).toFixed(2) : 0;
        s.recoCartCr = s.recoOpenCard ? +(100 * s.recoAddToCart / s.recoOpenCard).toFixed(2) : 0;
        s.recoOrderCr = s.recoOpenCard ? +(100 * s.recoOrders / s.recoOpenCard).toFixed(2) : 0;
        s.searchClickShare = s.openCardCount ? +(100 * s.searchOpenCard / s.openCardCount).toFixed(2) : 0;
        s.recoClickShare = s.openCardCount ? +(100 * s.recoOpenCard / s.openCardCount).toFixed(2) : 0;

        return s;
    }, [rows, filters.mp]);

    // Sort rows ASC by date for left-to-right display (oldest to newest)
    // Enrich each row with sppPercent computed from aggregated price values.
    const sortedRows = useMemo(() => [...rows].reverse().map(r => {
        const avgPrice = Number(r.ordersCount) ? Number(r.ordersSumRub) / Number(r.ordersCount) : 0;
        const avgPriceSpp = Number(r.sppQty) ? Number(r.sppSum) / Number(r.sppQty) : 0;
        const sppPercent = (avgPrice && Number(r.sppQty))
            ? +Math.max(0, ((avgPrice - avgPriceSpp) / avgPrice) * 100).toFixed(2)
            : 0;
        // Разбивка трафика WB: Поиск (wb_search_report_products) vs Рекомендации (воронка - поиск)
        const openCardCount = Number(r.openCardCount || 0);
        const addToCartCount = Number(r.addToCartCount || 0);
        const ordersCount = Number(r.ordersCount || 0);
        const searchOpenCard = Number(r.searchOpenCard || 0);
        const searchAddToCart = Number(r.searchAddToCart || 0);
        const searchOrders = Number(r.searchOrders || 0);
        const recoOpenCard = Math.max(0, openCardCount - searchOpenCard);
        const recoAddToCart = Math.max(0, addToCartCount - searchAddToCart);
        const recoOrders = Math.max(0, ordersCount - searchOrders);
        const searchCartCr = searchOpenCard > 0 ? +(100 * searchAddToCart / searchOpenCard).toFixed(2) : 0;
        const searchOrderCr = searchOpenCard > 0 ? +(100 * searchOrders / searchOpenCard).toFixed(2) : 0;
        const recoCartCr = recoOpenCard > 0 ? +(100 * recoAddToCart / recoOpenCard).toFixed(2) : 0;
        const recoOrderCr = recoOpenCard > 0 ? +(100 * recoOrders / recoOpenCard).toFixed(2) : 0;
        const searchClickShare = openCardCount > 0 ? +(100 * searchOpenCard / openCardCount).toFixed(2) : 0;
        const recoClickShare = openCardCount > 0 ? +(100 * recoOpenCard / openCardCount).toFixed(2) : 0;
        return {
            ...r, sppPercent,
            searchOpenCard, searchAddToCart, searchOrders,
            recoOpenCard, recoAddToCart, recoOrders,
            searchCartCr, searchOrderCr, recoCartCr, recoOrderCr,
            searchClickShare, recoClickShare,
        };
    }), [rows]);

    const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00C49F', '#FF8042', '#a4de6c', '#d0ed57', '#8dd1e1'];

    const toggleChartMetric = (key) => {
        setChartMetrics(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    const { chartData, chartRaw, chartDomain } = useMemo(() => {
        if (!sortedRows.length || !chartMetrics.length) return { chartData: [], chartRaw: [], chartDomain: [80, 120] };
        const means = {};
        for (const k of chartMetrics) means[k] = 0;
        const raw = sortedRows.map(r => {
            const point = { dt: (r.dt || '').slice(5) };
            for (const k of chartMetrics) {
                const v = Number(r[k] || 0);
                point[k] = v;
                means[k] += v;
            }
            return point;
        });
        const n = raw.length;
        for (const k of chartMetrics) means[k] = means[k] / n || 1;

        let globalMin = 100, globalMax = 100;
        const norm = raw.map(point => {
            const p = { dt: point.dt };
            for (const k of chartMetrics) {
                const pct = (point[k] / means[k]) * 100;
                p[k] = pct;
                if (pct < globalMin) globalMin = pct;
                if (pct > globalMax) globalMax = pct;
            }
            return p;
        });

        const pad = Math.max((globalMax - globalMin) * 0.15, 5);
        const domain = [Math.floor(globalMin - pad), Math.ceil(globalMax + pad)];

        return { chartData: norm, chartRaw: raw, chartDomain: domain };
    }, [sortedRows, chartMetrics]);

    const fmtMetricValue = (k, v) => {
        if (k.startsWith('ctr') || k.startsWith('cr') || k === 'buyoutPercent' || k === 'sppPercent' || k.endsWith('Conversion') || k.endsWith('Cr') || k.endsWith('Share') || k === 'roi' || k === 'advCtr' || k === 'drr') return fmtPct(v);
        if (k.includes('Sum') || k === 'cost' || k === 'comission' || k === 'logistic' || k === 'profit' || k === 'advCpc' || k === 'avgPrice' || k === 'avgPriceSpp') return fmtMoney(v);
        if (k === 'positionCategory') return fmtNum(v, 1);
        if (k === 'buyoutsCount') return fmtNum(v, 2);
        return fmtNum(v, 0);
    };

    const ChartTooltipContent = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const idx = chartData.findIndex(d => d.dt === label);
        const rawPoint = idx >= 0 ? chartRaw[idx] : null;
        return (
            <Paper sx={{ p: 1, fontSize: '0.8rem' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{`Дата: ${label}`}</Typography>
                {payload.map((entry) => (
                    <Box key={entry.dataKey} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color, flexShrink: 0 }} />
                        <span>{entry.name}: <b>{fmtMetricValue(entry.dataKey, rawPoint?.[entry.dataKey] ?? entry.value)}</b></span>
                    </Box>
                ))}
            </Paper>
        );
    };

    const metricLabel = (key) => {
        for (const g of Object.values(metricGroups)) {
            for (const m of g.metrics) { if (m.key === key) return m.label; }
        }
        return key;
    };

    const formatValue = (key, v) => {
        if (key === 'dt') return v;
        // CTR, CR, ROI and other percentages
        if (key.startsWith('ctr') || key.startsWith('cr') || key === 'buyoutPercent' || key === 'sppPercent' || key.endsWith('Conversion') || key.endsWith('Cr') || key.endsWith('Share') || key === 'roi' || key === 'advCtr' || key === 'drr') return fmtPct(v);
        // Money values
        if (key.includes('Sum') || key === 'cost' || key === 'comission' || key === 'logistic' || key === 'profit' || key === 'advCpc' || key === 'avgPrice' || key === 'avgPriceSpp') return fmtMoney(v);

        // Position with 1 decimal
        if (key === 'positionCategory') return fmtNum(v, 1);
        // Прогноз выкупов — дробный, показываем как есть
        if (key === 'buyoutsCount') return fmtNum(v, 2);
        return fmtNum(v, 0);
    };

    // Keys where lower is better (for color direction)
    const isLowerBetterKey = (key) => {
        return ['cost', 'comission', 'logistic', 'ndsSum', 'ndsOutSum', 'ndsInMpSum', 'ndsInProdSum', 'acquiringSum', 'marketDiscountSum', 'returnsCount', 'cancellationsCount', 'positionCategory', 'drr', 'advSum', 'advCpcSum', 'advCpoSum', 'advCpc', 'advShowsBoostSum', 'advBoostSalesSum', 'advShelfSum'].includes(key);
    };

    // Calculate delta percentage
    const calcDeltaPct = (curr, prev) => {
        if (prev == null || prev === 0 || curr == null) return null;
        return ((curr - prev) / Math.abs(prev)) * 100;
    };

    // Render cell with delta indicator
    const renderCellWithDelta = (key, currValue, prevValue) => {
        const formattedValue = formatValue(key, currValue);

        if (!showDelta || prevValue == null || currValue == null) {
            return formattedValue;
        }

        const deltaPct = calcDeltaPct(Number(currValue), Number(prevValue));
        if (deltaPct == null || !Number.isFinite(deltaPct) || Math.abs(deltaPct) < 0.01) {
            return formattedValue;
        }

        const lowerBetter = isLowerBetterKey(key);
        const isGood = lowerBetter ? deltaPct < 0 : deltaPct > 0;
        const color = isGood ? 'success.main' : 'error.main';
        const ArrowIcon = deltaPct > 0 ? ArrowDropUpIcon : ArrowDropDownIcon;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span>{formattedValue}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                    <ArrowIcon sx={{ color, fontSize: 16, m: -0.25 }} />
                    <Typography variant="caption" sx={{ color, fontSize: '0.65rem', lineHeight: 1 }}>
                        {fmtNum(Math.abs(deltaPct), 1)}%
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ежедневная аналитика
            </Typography>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <ProductFilters value={filters} onChange={setFilters} />
                <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={8} md={3.5}>
                        <DateRangeField
                            valueFrom={dateFrom}
                            valueTo={dateTo}
                            onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                    <Button variant="contained" onClick={load} disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}>
                        Загрузить
                    </Button>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={showDelta} onChange={() => setShowDelta(!showDelta)} />}
                        label={<Typography variant="body2">Изменения</Typography>}
                    />
                    {Object.entries(metricGroups).map(([groupKey, group]) => (
                        <FormControlLabel
                            key={groupKey}
                            control={<Checkbox size="small" checked={enabledGroups[groupKey]} onChange={() => toggleGroup(groupKey)} />}
                            label={<Typography variant="body2">{group.label}</Typography>}
                        />
                    ))}
                </Box>
            </Paper>

            {missingOutgoingNds.count > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>
                        Нет исходящего НДС у {missingOutgoingNds.count} {articleWord(missingOutgoingNds.count)}
                    </AlertTitle>
                    В расчёте с 1 сентября 2026 для них подставлен исходящий НДС 13%. Заполните поле в каталоге товаров. До этой даты НДС считается по ставке кабинета.
                    <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, maxHeight: 160, overflow: 'auto' }}>
                        {missingOutgoingNds.items.slice(0, 40).map((item, idx) => (
                            <li key={`${item.mp}-${item.organization}-${item.article}-${item.productId}-${idx}`}>
                                {item.mpLabel}{item.organization ? ` · ${item.organization}` : ''}: {item.article || item.productId || '—'}
                            </li>
                        ))}
                    </Box>
                    {missingOutgoingNds.items.length > 40 && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Показаны первые 40 из {missingOutgoingNds.items.length}
                            {missingOutgoingNds.count > missingOutgoingNds.items.length ? ` (всего ${missingOutgoingNds.count})` : ''}.
                        </Typography>
                    )}
                    {missingOutgoingNds.items.length <= 40 && missingOutgoingNds.count > missingOutgoingNds.items.length && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Показаны первые {missingOutgoingNds.items.length} из {missingOutgoingNds.count}.
                        </Typography>
                    )}
                </Alert>
            )}

            {/* Chart */}
            {sortedRows.length > 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    {chartMetrics.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {chartMetrics.map((k, i) => (
                                <Chip
                                    key={k}
                                    label={metricLabel(k)}
                                    size="small"
                                    sx={{ borderLeft: `4px solid ${CHART_COLORS[i % CHART_COLORS.length]}` }}
                                    onDelete={() => toggleChartMetric(k)}
                                />
                            ))}
                            {chartMetrics.length > 1 && (
                                <Chip label="Сбросить все" size="small" variant="outlined" onClick={() => setChartMetrics([])} onDelete={() => setChartMetrics([])} />
                            )}
                        </Box>
                    )}
                    {chartMetrics.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Нажмите на строку в таблице, чтобы добавить показатель на график
                        </Typography>
                    )}
                    <Box sx={{ height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                                <defs>
                                    {chartMetrics.map((k, i) => (
                                        <linearGradient key={k} id={`gradDaily_${k}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.25} />
                                            <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                                        </linearGradient>
                                    ))}
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="dt" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 11 }} width={40} domain={chartDomain} tickFormatter={v => `${v}%`} />
                                {chartMetrics.length > 0 && <ReferenceLine y={100} stroke="#999" strokeDasharray="4 4" label={{ value: 'среднее', position: 'right', fontSize: 10, fill: '#999' }} />}
                                <Tooltip content={<ChartTooltipContent />} />
                                <Legend />
                                {chartMetrics.map((k, i) => (
                                    <Area
                                        key={k}
                                        type="monotone"
                                        dataKey={k}
                                        name={metricLabel(k)}
                                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                                        strokeWidth={2}
                                        fill={`url(#gradDaily_${k})`}
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>
            )}

            {/* Transposed Table - dates as columns, metrics as rows */}
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headerStyle, position: 'sticky', left: 0, zIndex: 1, bgcolor: 'grey.100' }}>Показатель</TableCell>
                            {sortedRows.map(row => (
                                <TableCell key={row.dt} sx={headerStyle} align="right">{row.dt}</TableCell>
                            ))}
                            {sortedRows.length > 0 && <TableCell sx={{ ...headerStyle, fontWeight: 'bold' }} align="right">Итого</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(() => {
                            let lastGroup = null;
                            const groupColors = {
                                views: '#e3f2fd',
                                unique: '#fce4ec',
                                cart: '#fff3e0',
                                orders: '#e8f5e9',
                                traffic: '#ede7f6',
                                buyouts: '#f3e5f5',
                                advert: '#ffebee',
                                finance: '#fff8e1',
                                position: '#e0f7fa',
                            };

                            return headers.filter(h => h.key !== 'dt').map((h, idx) => {
                                const isFirstInGroup = h.group && h.group !== lastGroup;
                                lastGroup = h.group;
                                const groupColor = groupColors[h.group] || 'white';
                                const chartIdx = chartMetrics.indexOf(h.key);
                                const isOnChart = chartIdx !== -1;
                                const chartColor = isOnChart ? CHART_COLORS[chartIdx % CHART_COLORS.length] : null;

                                return (
                                    <TableRow
                                        key={h.key}
                                        hover
                                        sx={{
                                            borderTop: isFirstInGroup ? '2px solid #bdbdbd' : 'none',
                                            cursor: 'pointer',
                                            ...(isOnChart && { bgcolor: '#f0f0ff' }),
                                        }}
                                        onClick={() => toggleChartMetric(h.key)}
                                    >
                                        <TableCell
                                            sx={{
                                                ...cellStyle,
                                                position: 'sticky',
                                                left: 0,
                                                bgcolor: isOnChart ? '#e8e8ff' : (isFirstInGroup ? groupColor : '#fff'),
                                                fontWeight: isFirstInGroup ? 'bold' : 500,
                                                borderLeft: isOnChart ? `4px solid ${chartColor}` : `4px solid ${groupColor}`,
                                                zIndex: 1,
                                            }}
                                        >
                                            {h.hint ? (
                                                <MuiTooltip title={h.hint} placement="right" arrow>
                                                    <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}>{h.label}</Box>
                                                </MuiTooltip>
                                            ) : h.label}
                                        </TableCell>
                                        {sortedRows.map((row, rowIdx) => {
                                            // Previous row (before in array since sorted ASC by date)
                                            const prevRow = sortedRows[rowIdx - 1];
                                            const currValue = row[h.key];
                                            const prevValue = prevRow ? prevRow[h.key] : null;

                                            return (
                                                <TableCell
                                                    key={`${row.dt}-${h.key}`}
                                                    sx={{ ...cellStyle, bgcolor: isFirstInGroup ? groupColor : 'inherit' }}
                                                    align="right"
                                                >
                                                    {renderCellWithDelta(h.key, currValue, prevValue)}
                                                </TableCell>
                                            );
                                        })}
                                        {sortedRows.length > 0 && (
                                            <TableCell
                                                sx={{
                                                    ...cellStyle,
                                                    fontWeight: 'bold',
                                                    bgcolor: isFirstInGroup ? groupColor : 'grey.100'
                                                }}
                                                align="right"
                                            >
                                                {formatValue(h.key, totals[h.key])}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            });
                        })()}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
